/**
 * plansUtils.js
 *
 * Date + maths layer for the Plans feature (RRSP / TFSA contribution tracking).
 *
 * Design notes
 * ------------
 * Future contributions are NEVER stored. A "source" describes a recurring
 * intent ($190 every 2 weeks from Mar 3). Upcoming occurrences are generated
 * from that description on demand. Only things that actually happened get
 * persisted: confirmed contributions, skipped occurrences, and one-offs.
 *
 * That means changing an amount, pausing, or stopping a source needs no
 * rewriting of pending rows — you edit the source and the future regenerates.
 *
 * All dates are handled in UTC and passed around as 'YYYY-MM-DD' strings to
 * avoid timezone drift shifting a contribution across a year boundary.
 */

/* ------------------------------------------------------------------ *
 * Date helpers — everything is a 'YYYY-MM-DD' string at the boundary
 * ------------------------------------------------------------------ */

export function toKey(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

/**
 * Today, in the device's own timezone.
 *
 * NOT toKey(new Date()). toKey reads UTC components, which is right for keys
 * produced by fromKey - those are UTC by construction so a date can never drift
 * across a year boundary - and wrong for a live instant. Anywhere west of UTC,
 * every evening past UTC midnight rolls the key on to tomorrow: in Toronto that
 * is everything after 8pm, when the app would start calling tomorrow "today".
 */
export function todayLocalKey(d) {
  const now = d || new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

/**
 * Now, as local wall-clock 'YYYY-MM-DDTHH:mm'.
 *
 * Reminder dueAt values are local wall-clock - 08:30 means half past eight
 * where you are, not in UTC - so the thing they are compared against has to be
 * too. toISOString() is UTC and made everything look hours out.
 */
export function nowLocalIso(d) {
  const now = d || new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return todayLocalKey(now) + 'T' + hh + ':' + mm;
}

export function fromKey(key) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

export function addDays(key, n) {
  const d = fromKey(key);
  d.setUTCDate(d.getUTCDate() + n);
  return toKey(d);
}

export function daysBetween(aKey, bKey) {
  return Math.round((fromKey(bKey) - fromKey(aKey)) / 86400000);
}

export function isLeapYear(y) {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

/** Last calendar day of a given month (1-indexed month). */
export function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Roll a date forward to the next weekday if it lands on Sat/Sun. */
export function rollToBusinessDay(key) {
  const d = fromKey(key);
  const dow = d.getUTCDay(); // 0 Sun, 6 Sat
  if (dow === 0) return addDays(key, 1);
  if (dow === 6) return addDays(key, 2);
  return key;
}

/* ------------------------------------------------------------------ *
 * Contribution windows
 * ------------------------------------------------------------------ */

/**
 * The RRSP deadline for a tax year is the 60th day of the FOLLOWING calendar
 * year, rolled forward if it falls on a weekend.
 *
 *   day 1  = Jan 1
 *   day 59 = Feb 28
 *   day 60 = Feb 29 in a leap year, otherwise Mar 1
 *
 * e.g. taxYear 2026 -> 60th day of 2027 is Mar 1, 2027 (a Monday) -> Mar 1, 2027
 *      taxYear 2025 -> 60th day of 2026 is Mar 1, 2026 (a Sunday) -> Mar 2, 2026
 */
export function getRrspDeadline(taxYear) {
  const y = taxYear + 1;
  const key = isLeapYear(y) ? y + '-02-29' : y + '-03-01';
  return rollToBusinessDay(key);
}

/**
 * The contribution window for a tax year opens the day after the previous
 * year's deadline and closes on this year's deadline.
 */
export function getRrspWindow(taxYear) {
  return {
    account: 'rrsp',
    taxYear,
    start: addDays(getRrspDeadline(taxYear - 1), 1),
    end: getRrspDeadline(taxYear),
  };
}

/** TFSA is a plain calendar year. */
export function getTfsaWindow(taxYear) {
  return {
    account: 'tfsa',
    taxYear,
    start: taxYear + '-01-01',
    end: taxYear + '-12-31',
  };
}

export function getWindow(account, taxYear) {
  return account === 'rrsp' ? getRrspWindow(taxYear) : getTfsaWindow(taxYear);
}

export function isInWindow(dateKey, win) {
  return dateKey >= win.start && dateKey <= win.end;
}

/**
 * True while two RRSP windows overlap — Jan 1 through the deadline, when a
 * contribution could be claimed against either year. The UI uses this to offer
 * the "count toward" choice instead of assuming.
 */
export function isAmbiguousRrspDate(dateKey) {
  const y = fromKey(dateKey).getUTCFullYear();
  return dateKey >= y + '-01-01' && dateKey <= getRrspDeadline(y - 1);
}

/* ------------------------------------------------------------------ *
 * Sources -> occurrences
 * ------------------------------------------------------------------ */

export const FREQUENCIES = ['monthly', 'biweekly', 'semimonthly'];

/**
 * The amount a source is set to on a given date, honouring scheduled changes.
 * source.changes = [{ effectiveDate, amount }] — the latest one on or before
 * the date wins.
 */
export function amountOn(source, dateKey) {
  let amount = Number(source.amount) || 0;
  const changes = (source.changes || [])
    .slice()
    .sort((a, b) => (a.effectiveDate < b.effectiveDate ? -1 : 1));
  for (const c of changes) {
    if (c.effectiveDate <= dateKey) amount = Number(c.amount) || 0;
  }
  return amount;
}

/**
 * Every date this source fires between two bounds.
 * Respects the source's own start date and, if stopped, its stop date.
 */
export function occurrenceDates(source, fromKeyStr, toKeyStr) {
  const out = [];
  const begin = source.startDate > fromKeyStr ? source.startDate : fromKeyStr;

  // Three things can cut a source short: the window we're asking about, a
  // scheduled end date, and being stopped. The earliest wins - a source that
  // was due to end in June but got stopped in April really ended in April.
  let stop = toKeyStr;
  if (source.endDate && source.endDate < stop) stop = source.endDate;
  if (source.status === 'stopped' && source.stoppedDate && source.stoppedDate < stop) {
    stop = source.stoppedDate;
  }
  if (begin > stop) return out;

  if (source.frequency === 'biweekly') {
    // Anchored to startDate, every 14 days. Walk forward from the anchor so
    // the cadence never drifts, even when the window starts mid-cycle.
    let cur = source.startDate;
    while (cur < begin) cur = addDays(cur, 14);
    while (cur <= stop) {
      out.push(cur);
      cur = addDays(cur, 14);
    }
    return out;
  }

  if (source.frequency === 'semimonthly') {
    let d = fromKey(begin);
    let y = d.getUTCFullYear();
    let m = d.getUTCMonth() + 1;
    while (true) {
      const first = y + '-' + String(m).padStart(2, '0') + '-15';
      const last = y + '-' + String(m).padStart(2, '0') + '-' +
        String(lastDayOfMonth(y, m)).padStart(2, '0');
      for (const k of [first, last]) {
        if (k >= begin && k <= stop) out.push(k);
      }
      if (last > stop) break;
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }
    return out;
  }

  // monthly — same day of month as startDate, clamped to short months
  const anchorDay = fromKey(source.startDate).getUTCDate();
  let d = fromKey(begin);
  let y = d.getUTCFullYear();
  let m = d.getUTCMonth() + 1;
  while (true) {
    const day = Math.min(anchorDay, lastDayOfMonth(y, m));
    const k = y + '-' + String(m).padStart(2, '0') + '-' + String(day).padStart(2, '0');
    if (k > stop) break;
    if (k >= begin) out.push(k);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

/** Stable id for a scheduled slot, so confirm/skip can point at it. */
export function occurrenceId(sourceId, dateKey) {
  return sourceId + ':' + dateKey;
}

/**
 * All occurrences for a set of sources inside a window, each tagged with its
 * state: 'confirmed', 'skipped', 'due' (date has passed, no decision yet), or
 * 'upcoming'.
 *
 * contributions = [{ id, sourceId, occurrenceId, account, amount, date,
 *                    taxYear, status: 'confirmed'|'skipped', type, note }]
 */
export function buildOccurrences(sources, contributions, win, todayKey) {
  const logged = {};
  for (const c of contributions || []) {
    if (c.occurrenceId) logged[c.occurrenceId] = c;
  }

  const rows = [];
  for (const s of sources || []) {
    if (s.account !== win.account) continue;
    for (const dateKey of occurrenceDates(s, win.start, win.end)) {
      const oid = occurrenceId(s.id, dateKey);
      const hit = logged[oid];
      const planned = amountOn(s, dateKey);
      let state;
      if (hit) state = hit.status === 'skipped' ? 'skipped' : 'confirmed';
      else state = dateKey <= todayKey ? 'due' : 'upcoming';

      rows.push({
        occurrenceId: oid,
        sourceId: s.id,
        sourceName: s.name,
        account: s.account,
        date: dateKey,
        plannedAmount: planned,
        amount: hit && hit.status === 'confirmed' ? Number(hit.amount) : planned,
        state,
        contributionId: hit ? hit.id : null,
      });
    }
  }
  rows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return rows;
}

/** Occurrences whose date has arrived and that still need a yes or no. */
export function pendingOccurrences(sources, contributions, win, todayKey) {
  return buildOccurrences(sources, contributions, win, todayKey)
    .filter((o) => o.state === 'due');
}

/* ------------------------------------------------------------------ *
 * Totals
 * ------------------------------------------------------------------ */

/**
 * Everything the Overview tab needs for one account.
 *
 *   confirmed  — actually in, including starting balances and one-offs
 *   pending    — due but undecided
 *   upcoming   — still to come before the window closes
 *   projected  — confirmed + pending + upcoming, if nothing changes
 */
export function summarize(opts) {
  const {
    account,
    taxYear,
    sources = [],
    contributions = [],
    target = 0,
    roomAvailable = null,
    // Contributions already made this year that no source accounts for -
    // the only way to record anything at all when there is no source.
    opening = 0,
    todayKey,
  } = opts;

  const win = getWindow(account, taxYear);
  const mine = sources.filter((s) => s.account === account);
  const occ = buildOccurrences(mine, contributions, win, todayKey);

  // Starting balances: what was already contributed before tracking began.
  // This is a fact about ONE year, not a standing property of the source, so
  // it only counts in the window the source's startDate falls into. Without
  // that check it is re-counted in every tax year forever.
  let confirmed = (Number(opening) || 0) +
    mine.reduce(
      (sum, s) =>
        s.startDate && isInWindow(s.startDate, win)
          ? sum + (Number(s.startingAmount) || 0)
          : sum,
      0
    );

  // One-offs and manual logs — anything without an occurrenceId, claimed
  // against this tax year.
  for (const c of contributions) {
    if (c.account !== account) continue;
    if (c.occurrenceId) continue;
    if (c.status === 'skipped') continue;
    const claimed = c.taxYear != null ? c.taxYear : taxYear;
    if (claimed !== taxYear) continue;
    confirmed += Number(c.amount) || 0;
  }

  let pending = 0;
  let upcoming = 0;
  for (const o of occ) {
    if (o.state === 'confirmed') confirmed += o.amount;
    else if (o.state === 'due') pending += o.plannedAmount;
    else if (o.state === 'upcoming') upcoming += o.plannedAmount;
  }

  const totalDays = daysBetween(win.start, win.end) || 1;
  const elapsedDays = Math.min(Math.max(daysBetween(win.start, todayKey), 0), totalDays);

  // One-offs attached to a source count toward that source's own total. The
  // account total above already counted them, so this is presentation only -
  // it must never be added into `confirmed` a second time.
  const oneOffBySource = {};
  for (const c of contributions) {
    if (c.account !== account || c.occurrenceId || c.status === 'skipped') continue;
    if (!c.sourceId) continue;
    const claimed = c.taxYear != null ? c.taxYear : taxYear;
    if (claimed !== taxYear) continue;
    oneOffBySource[c.sourceId] = (oneOffBySource[c.sourceId] || 0) + (Number(c.amount) || 0);
  }

  const perSource = mine.map((s) => {
    const rows = occ.filter((o) => o.sourceId === s.id && o.state === 'confirmed');
    return {
      id: s.id,
      name: s.name,
      status: s.status || 'active',
      confirmed:
        rows.reduce((n, r) => n + r.amount, 0) +
        (oneOffBySource[s.id] || 0) +
        (s.startDate && isInWindow(s.startDate, win)
          ? Number(s.startingAmount) || 0
          : 0),
      nextDate: (occ.find((o) => o.sourceId === s.id && o.state === 'upcoming') || {}).date || null,
      dueCount: occ.filter((o) => o.sourceId === s.id && o.state === 'due').length,
    };
  });

  const projected = confirmed + pending + upcoming;

  return {
    window: win,
    confirmed,
    pending,
    upcoming,
    projected,
    target: Number(target) || 0,
    targetLeft: Math.max(0, (Number(target) || 0) - confirmed),
    targetPct: target > 0 ? Math.min(100, Math.round((confirmed / target) * 100)) : 0,
    windowPct: Math.round((elapsedDays / totalDays) * 100),
    daysLeft: Math.max(0, totalDays - elapsedDays),
    roomAvailable,
    roomLeft: roomAvailable == null ? null : Math.max(0, roomAvailable - confirmed),
    // Deliberately NOT clamped at zero: going negative is the whole signal,
    // and it is how an over-contribution becomes visible before it happens.
    roomAfterPlan: roomAvailable == null ? null : roomAvailable - projected,
    occurrences: occ,
    perSource,
  };
}

/**
 * Are they keeping up? Compared against how much of the window has gone by,
 * with a 5-point tolerance so it doesn't flip to a warning over rounding.
 */
export function paceStatus(summary) {
  if (!summary.target) return { key: 'none', label: 'No target set' };
  // Percentage points between how much of the window has gone and how much of
  // the target is in. One "behind" bucket was not enough - being 6 points down
  // and being 37 points down are not the same situation.
  const gap = summary.targetPct - summary.windowPct;
  if (gap >= 5) return { key: 'ahead', label: 'Ahead' };
  if (gap >= -5) return { key: 'ontrack', label: 'On track' };
  if (gap >= -15) return { key: 'slightly', label: 'Slightly behind' };
  return { key: 'behind', label: 'Behind' };
}

/* ------------------------------------------------------------------ *
 * Budget transfers -> suggested contributions
 * ------------------------------------------------------------------ */

/** How far either side of a scheduled date a transfer can land and still match. */
export const TRANSFER_MATCH_DAYS = 7;

/** ...and how far from the planned amount, in dollars. */
export const TRANSFER_MATCH_AMOUNT = 5;

/**
 * Transfers before this are never suggested. Anything earlier was already
 * recorded as a starting amount or logged by hand before this existed.
 * plans.transferTrackingSince (set on first link) wins; this is the fallback
 * for anyone who linked before that stamp existed - the day it shipped.
 */
export const TRANSFER_TRACKING_START = '2026-09-24';

/**
 * Budget transfers into the investment accounts linked to RRSP / TFSA, turned
 * into suggestions. READ ONLY - nothing here writes anywhere.
 *
 * Each suggestion is one of:
 *   { kind: 'match',  account, transfer, occ }   - a scheduled slot within 7 days
 *   { kind: 'oneoff', account, transfer }        - nothing scheduled nearby
 *
 * Left out, so linking an account never duplicates anything:
 *   - transfers already turned into a contribution (contribution.transactionId)
 *   - transfers the user chose to ignore (plans.ignoredTransferIds)
 *   - transfers near a slot that was already confirmed by hand, or near a
 *     hand-logged one-off of the same amount - those were recorded already.
 *
 * transfer = { id, accountId, amount, date }
 */
export function findTransferSuggestions({ plans, transactions, taxYear, todayKey }) {
  const p = plans || {};
  const accts = p.accounts || {};
  const contributions = p.contributions || [];
  const ignored = new Set((p.ignoredTransferIds || []).map(String));
  const used = new Set(contributions.map((c) => c.transactionId).filter(Boolean).map(String));
  // Pairs the user said were NOT the same thing ('transactionId|occurrenceId').
  const rejected = new Set((p.rejectedMatches || []).map(String));
  const since = p.transferTrackingSince || TRANSFER_TRACKING_START;
  const byId = {};
  for (const c of contributions) byId[c.id] = c;
  const out = [];

  for (const account of ['rrsp', 'tfsa']) {
    const linked = new Set(((accts[account] || {}).linkedAccountIds || []).map(String));
    if (!linked.size) continue;
    const win = getWindow(account, taxYear);

    const transfers = (transactions || [])
      .filter((t) => t && t.id != null && t.date
        && t.account_type === 'investment'
        && t.transaction_type === 'deposit'
        && linked.has(String(t.account_id))
        && (Number(t.amount) || 0) > 0)
      .map((t) => ({ id: String(t.id), accountId: t.account_id, amount: Number(t.amount) || 0, date: String(t.date).slice(0, 10) }))
      .filter((t) => isInWindow(t.date, win) && t.date >= since && t.date <= todayKey && !ignored.has(t.id) && !used.has(t.id))
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    if (!transfers.length) continue;

    const occ = buildOccurrences((p.sources || []).filter((s) => s.account === account), contributions, win, todayKey);
    const taken = new Set();
  
    for (const t of transfers) {
      const near = occ.filter((o) => !taken.has(o.occurrenceId)
        && !rejected.has(t.id + '|' + o.occurrenceId)
        && Math.abs(daysBetween(o.date, t.date)) <= TRANSFER_MATCH_DAYS
        && Math.abs((Number(o.plannedAmount) || 0) - t.amount) <= TRANSFER_MATCH_AMOUNT);

      // Everything after linking is shown. Better to offer one you dismiss
      // than to hide one you then have to type in by hand.
      const open = near.filter((o) => o.state === 'due' || o.state === 'upcoming');
      if (open.length) {
        open.sort((a, b) => {
          const d = Math.abs(daysBetween(a.date, t.date)) - Math.abs(daysBetween(b.date, t.date));
          if (d !== 0) return d;
          return Math.abs(a.plannedAmount - t.amount) - Math.abs(b.plannedAmount - t.amount);
        });
        taken.add(open[0].occurrenceId);
        out.push({ kind: 'match', account, transfer: t, occ: open[0] });
      } else {
        out.push({ kind: 'oneoff', account, transfer: t });
      }
    }
  }
  out.sort((a, b) => (a.transfer.date < b.transfer.date ? -1 : 1));
  return out;
}

/**
 * Everything waiting for a decision, each thing once:
 *   { kind: 'match',    date, occ, transfer }  scheduled + transfer found
 *   { kind: 'due',      date, occ }            scheduled, date passed, no transfer
 *   { kind: 'transfer', date, transfer, account }  transfer, nothing scheduled
 * Oldest first. The Home insight counts this same list.
 */
export function buildReviewItems({ plans, transactions, taxYear, todayKey, dueOccurrences }) {
  const sugg = findTransferSuggestions({ plans, transactions, taxYear, todayKey });
  const matched = new Set(sugg.filter((s) => s.kind === 'match').map((s) => s.occ.occurrenceId));
  const items = [];
  for (const s of sugg) {
    if (s.kind === 'match') items.push({ kind: 'match', date: s.occ.date, occ: s.occ, transfer: s.transfer, account: s.account });
    else items.push({ kind: 'transfer', date: s.transfer.date, transfer: s.transfer, account: s.account });
  }
  for (const o of dueOccurrences || []) {
    if (!matched.has(o.occurrenceId)) items.push({ kind: 'due', date: o.date, occ: o, account: o.account });
  }
  items.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return items;
}

/* ------------------------------------------------------------------ *
 * Shapes
 * ------------------------------------------------------------------ */

export const EMPTY_PLANS = {
  version: 1,
  accounts: {
    // linkedAccountIds: Wealth investment accounts whose Budget transfers
    // are suggested as contributions (see findTransferSuggestions).
    rrsp: { target: 0, roomAvailable: null, linkedAccountIds: [] },
    tfsa: { target: 0, roomAvailable: null, linkedAccountIds: [] },
  },
  // Budget transaction ids the user chose not to count.
  ignoredTransferIds: [],
  // 'transactionId|occurrenceId' pairs the user said don't belong together.
  rejectedMatches: [],
  sources: [],
  contributions: [],
  notes: [],
  // { id, name, color } where color is an index into the tile palette, so
  // folder colours follow the theme instead of being baked in as hex.
  folders: [],
  // { id, title, note, link, dueAt, done, doneAt }
  reminders: [],
  milestones: [],
  // Budgets tab on Notes & Reminders - see budgetSheetMath.js for the shape.
  budgetSheets: [],
};

export function newSource(overrides) {
  return Object.assign({
    id: 'src_' + Math.random().toString(36).slice(2, 10),
    account: 'rrsp',
    name: '',
    amount: 0,
    frequency: 'monthly',
    startDate: null,
    endDate: null,
    startingAmount: 0,
    status: 'active',
    stoppedDate: null,
    changes: [],
  }, overrides || {});
}

export function newContribution(overrides) {
  return Object.assign({
    id: 'con_' + Math.random().toString(36).slice(2, 10),
    sourceId: null,
    occurrenceId: null,
    account: 'rrsp',
    amount: 0,
    date: null,
    taxYear: null,
    status: 'confirmed',
    type: 'oneoff',
    note: '',
    // Set when the contribution came from a Budget transfer.
    transactionId: null,
  }, overrides || {});
}
